import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { toast } from "sonner";
import {
  PlusIcon, ArrowClockwiseIcon, ArrowsClockwiseIcon, PackageIcon, MagnifyingGlassIcon,
  LightningIcon, WarningCircleIcon, WarningIcon, CheckCircleIcon, XIcon,
  PrinterIcon, StackIcon, TreeStructureIcon, TrashIcon, CopyIcon, EyeIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateIST } from "@/lib/datetime";

// Feb 2026 · MRP (Material Requirements Planning) — pick a Sales Order,
// its child Work Order + active BOM auto-resolve, hit Calculate to see
// the requirement table, hit Process MRP to persist a run + reserve
// available stock + generate a Purchase Requirement for shortages.

const fmt = (n, d = 3) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const btnPrimary = "inline-flex items-center gap-1.5 h-9 px-4 bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest rounded-sm hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed";
const btnGhost = "inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--border)] text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-[var(--bg-2)] transition";

const STATUS_STYLE = {
  "AVAILABLE": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  "PARTIALLY AVAILABLE": "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  "PURCHASE REQUIRED": "bg-red-500/15 text-red-300 border-red-500/40",
  "COVERED BY OPEN PO": "bg-blue-500/15 text-blue-300 border-blue-500/40",
};

export default function MRP() {
  const [candidates, setCandidates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);      // candidate row
  const [preview, setPreview] = useState(null);        // calc result
  const [detail, setDetail] = useState(null);          // existing MRP run
  // Feb 2026 · Hierarchical explode for the detail view — reuses the
  // BOM explode endpoint so we get Level 1 (SF sub-assemblies) + Level 2
  // (RM leaves) sections that mirror the "All BOMs" view. Overlaid with
  // the run's stored `lines[]` so RM rows still show free/open-PO/net.
  const [detailLevels, setDetailLevels] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  // Mar 03, 2026 · Manual Allocation Control state — per-RM {ticked, qty}.
  const [allocations, setAllocations] = useState({});
  const [batchPopup, setBatchPopup] = useState(null); // { ln, idx }
  // Feb 2026 · Duplicate-PR clash resolver — populated on 409.
  const [prClash, setPrClash] = useState(null); // { message, clashes[] }
  // Feb 2026 · Pick-up flow — planner selects an SO/ESO, WO auto-narrows
  // to that SO's Work Orders. Runs alongside the browse table below.
  const [pickSoId, setPickSoId] = useState("");
  const [pickWoId, setPickWoId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: r }] = await Promise.all([api.get("/mrp/candidates"), api.get("/mrp/")]);
      setCandidates(c || []); setRuns(r || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load MRP data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · Delete an MRP run — cascades to remove its generated
  // Purchase Requirement so nothing is left dangling in the ledger.
  const removeRun = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${r.mrp_no}? Its Purchase Requirement (${r.pr_no || "none"}) will also be removed.`)) return;
    try {
      await api.delete(`/mrp/${r.id}`);
      toast.success(`${r.mrp_no} · deleted`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return candidates;
    return candidates.filter(c =>
      (c.sales_order_no || "").toLowerCase().includes(n) ||
      (c.work_order_no || "").toLowerCase().includes(n) ||
      (c.customer || "").toLowerCase().includes(n) ||
      (c.product_code || "").toLowerCase().includes(n));
  }, [candidates, q]);

  // ── Pick-up flow ──────────────────────────────────────────────
  // Distinct SO / ESO list drawn from the same candidates payload so
  // planners only ever see orders that already have a Work Order.
  const soOptions = useMemo(() => {
    const seen = new Map();
    candidates.forEach(c => {
      if (c.sales_order_id && !seen.has(c.sales_order_id)) {
        seen.set(c.sales_order_id, {
          id: c.sales_order_id,
          no: c.sales_order_no || "—",
          customer: c.customer || "",
          type: (c.sales_order_no || "").startsWith("ESO") ? "ESO" : "SO",
        });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.no.localeCompare(b.no));
  }, [candidates]);
  const woOptions = useMemo(
    () => candidates.filter(c => c.sales_order_id === pickSoId),
    [candidates, pickSoId],
  );
  useEffect(() => {
    // Auto-pick the WO when the chosen SO has exactly one child. Clear
    // otherwise so the planner has to make an explicit choice.
    if (pickSoId && woOptions.length === 1) setPickWoId(woOptions[0].work_order_id);
    else setPickWoId("");
  }, [pickSoId, woOptions.length]); // eslint-disable-line
  const picked = useMemo(
    () => candidates.find(c => c.work_order_id === pickWoId) || null,
    [candidates, pickWoId],
  );

  const calculate = async (row) => {
    setSelected(row); setPreview(null); setAllocations({}); setLoading(true);
    try {
      const { data } = await api.post("/mrp/calculate", { sales_order_id: row.sales_order_id, work_order_id: row.work_order_id });
      setPreview(data);
      // Feb 2026 · Intelligent Auto-Allocation — pre-tick lots that came
      // in via a PO linked to this SO, or opening-stock lots reserved
      // for it. Cap total per line at gross_qty so we never over-reserve.
      const autoAlloc = {};
      (data.lines || []).forEach(ln => {
        const eligible = (ln.lots || []).filter(l => l.auto_reason && Number(l.qty_available) > 0);
        if (eligible.length === 0) return;
        const gross = Number(ln.gross_qty || 0);
        const free = Number(ln.free_stock || 0);
        const cap = Math.min(gross, free);
        let remaining = cap;
        const sel = {};
        const reasons = new Set();
        for (const l of eligible) {
          if (remaining <= 0) break;
          const take = Math.min(Number(l.qty_available) || 0, remaining);
          if (take > 0) {
            sel[l.lot_id || l.lot_no] = round3(take);
            remaining -= take;
            reasons.add(l.auto_reason);
          }
        }
        const total = Object.values(sel).reduce((s, v) => s + v, 0);
        if (total > 0) {
          autoAlloc[ln.rm_id] = {
            ticked: true, qty: round3(total), selected_lots: sel,
            auto: true, auto_reasons: [...reasons],
          };
        }
      });
      if (Object.keys(autoAlloc).length > 0) {
        setAllocations(autoAlloc);
        toast.success(`Auto-allocated ${Object.keys(autoAlloc).length} line(s) from linked stock`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculate failed");
      setSelected(null);
    } finally { setLoading(false); }
  };

  const process = async (force = false, forceDup = false, overwrite = false) => {
    if (!selected) return;
    // Mar 03, 2026 · Mandatory reason enforcement — refuse to submit
    // when the operator left an under-allocated RM without a reason.
    // Feb 2026 · Enforce non-allocation reason ONLY when the operator has
    // free stock and hasn't fully covered the requirement (alloc<gross). If
    // alloc>=gross the line is closed; the surplus free stock doesn't need
    // justification — it's just idle inventory.
    if (preview && Array.isArray(preview.lines)) {
      const missing = preview.lines.find(ln => {
        const s = allocations[ln.rm_id] || { ticked: false, qty: 0 };
        const grossCap = Number(ln.gross_qty || 0);
        const capBoth = Math.min(Number(ln.free_stock || 0), grossCap);
        const alloc = s.ticked ? Math.max(0, Math.min(Number(s.qty) || 0, capBoth)) : 0;
        return Number(ln.free_stock || 0) > 0 && alloc < grossCap && alloc < Number(ln.free_stock || 0) && !(s.reason || "");
      });
      if (missing) {
        toast.error(`Non-Allocation Reason required for ${missing.rm_code}`);
        return;
      }
    }
    setProcessing(true);
    try {
      // Feb 2026 · Cap final payload qty per line at min(free_stock, gross_qty)
      // so the backend never over-reserves even if state briefly held a raw
      // batch-tick total before the derived alloc trimmed it.
      const lineCap = Object.fromEntries((preview?.lines || []).map(ln => [
        ln.rm_id,
        Math.min(Number(ln.free_stock || 0), Number(ln.gross_qty || 0)),
      ]));
      const alloc_payload = Object.entries(allocations).map(([rm_id, v]) => {
        const cap = lineCap[rm_id] ?? Number(v.qty) ?? 0;
        const cappedQty = v.ticked ? Math.max(0, Math.min(Number(v.qty) || 0, cap)) : 0;
        return {
          rm_id,
          allocated_qty: cappedQty,
          non_allocation_reason: v.reason || "",
          reason_note: v.reason_note || "",
          selected_lots: Object.entries(v.selected_lots || {}).map(([lot_id, qty]) => ({ lot_id, qty: Number(qty) || 0 })),
          is_auto: !!v.auto,
          auto_reasons: v.auto_reasons || [],
        };
      });
      const { data } = await api.post("/mrp/process", {
        sales_order_id: selected.sales_order_id,
        work_order_id: selected.work_order_id,
        reserve_stock: true, create_pr: true,
        force_rerun: force, force_duplicate: forceDup, overwrite_pr: overwrite,
        manual_allocations: alloc_payload,
      });
      const tail = data.pr_no ? ` · PR ${data.pr_no} generated` : "";
      const sup = (data.superseded_pr_nos || []).length
        ? ` · superseded ${data.superseded_pr_nos.join(", ")}` : "";
      toast.success(`MRP ${data.mrp_no} processed${tail}${sup}`);
      setSelected(null); setPreview(null); setPrClash(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // "already processed" → text detail, ask for re-run
      if (typeof detail === "string" && detail.includes("already processed") && window.confirm(detail + "\n\nRe-run anyway?")) {
        setProcessing(false); return process(true, forceDup, overwrite);
      }
      // Structured 409 → open the 3-choice PR-clash modal
      if (status === 409 && detail && typeof detail === "object" && Array.isArray(detail.clashes)) {
        setPrClash(detail);
        return;
      }
      // Legacy string 409 fallback (older builds).
      if (status === 409 && typeof detail === "string" && detail.includes("already exists")) {
        setPrClash({ message: detail, clashes: [] });
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Process failed"));
    } finally { setProcessing(false); }
  };

  // Phase H1-γ-β · Confirm using the server-proposed EXACT + APPROVED_ALTERNATE
  // allocations returned by /mrp/calculate.  Sends a client-generated
  // confirmation_id so the multi-leg saga is idempotent under retries; the
  // server independently revalidates every alternate RMA before reserving.
  const processWithAlternates = async (force = false) => {
    if (!preview || !selected) return;
    const legs = (preview.lines || [])
      .filter(l => (l.proposed_allocations || []).length > 0)
      .map(l => ({
        required_rm_id: l.rm_id,
        allocations: (l.proposed_allocations || []).map(pa => ({
          allocation_type: pa.allocation_type,
          allocated_rm_id: pa.allocated_rm_id,
          allocated_qty: Number(pa.allocated_qty) || 0,
          alternate_approval_id: pa.alternate_approval_id || "",
        })),
      }));
    if (legs.length === 0) {
      toast.error("No proposed allocations to confirm");
      return;
    }
    const confId = (window.crypto?.randomUUID?.() || `conf-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setProcessing(true);
    try {
      const { data } = await api.post("/mrp/process", {
        sales_order_id: selected.sales_order_id,
        work_order_id: selected.work_order_id,
        reserve_stock: true, create_pr: true,
        force_rerun: force,
        alternate_confirmations: legs,
        confirmation_id: confId,
      });
      const tail = data.pr_no ? ` · PR ${data.pr_no}` : "";
      const altCount = (data.committed_legs || []).filter(c => c.allocation_type === "APPROVED_ALTERNATE").length;
      const altTail = altCount ? ` · ${altCount} alternate leg(s)` : "";
      toast.success(`MRP ${data.mrp_no} confirmed${tail}${altTail}`);
      setSelected(null); setPreview(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (typeof detail === "string" && detail.includes("already processed") && window.confirm(detail + "\n\nRe-run anyway?")) {
        setProcessing(false); return processWithAlternates(true);
      }
      if (status === 409 && detail && typeof detail === "object") {
        if (detail.code === "STOCK_CHANGED") {
          toast.error(`Free stock changed for ${detail.rm_code || detail.rm_id} · ${detail.available_now} available. Please refresh.`);
        } else if (detail.code === "RMA_STALE") {
          toast.error(`RMA ${detail.rma_no || detail.rma_id} is stale (${detail.reason}). Refresh preview.`);
        } else if (detail.code === "IDEMPOTENCY_CONFLICT") {
          toast.error("Duplicate confirmation_id with different payload. Refresh and retry.");
        } else {
          toast.error(detail.message || JSON.stringify(detail));
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Confirmation failed"));
    } finally { setProcessing(false); }
  };

  // Feb 2026 · Open MRP detail with hierarchical L1+L2 explode. Fires
  // /boms/{id}/explode against the run's snapshotted bom_id + prod_qty
  // and returns levels[] the dialog renders as separate sections. On
  // failure we still open the dialog with the flat RM list.
  const openDetail = async (mrp) => {
    setDetail(mrp);
    setDetailLevels(null);
    if (!mrp?.bom_id) return;
    setDetailLoading(true);
    try {
      const { data } = await api.post(`/boms/${mrp.bom_id}/explode`, { qty: Number(mrp.production_qty) || 1 });
      setDetailLevels(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Explode failed — showing flat RM list");
    } finally { setDetailLoading(false); }
  };

  const closeDetail = () => { setDetail(null); setDetailLevels(null); };

  const printMrpPdf = async () => {
    if (!detail?.bom_id) return toast.error("MRP run has no linked BOM");
    try {
      const res = await api.get(`/boms/${detail.bom_id}/pdf`, {
        params: { qty: Number(detail.production_qty) || 1 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };

  // Overlay stored MRP `lines[]` (net_requirement etc.) onto RM leaves
  // of the explode result, keyed by rm_id, so the hierarchical view
  // still shows Free / Open PO / Net Req in Level 2 sections.
  const runLineByRmId = useMemo(() => {
    const m = new Map();
    (detail?.lines || []).forEach(l => l.rm_id && m.set(l.rm_id, l));
    return m;
  }, [detail]);

  return (
    <>
      <div className="p-6 space-y-4" data-testid="mrp-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Production</div>
            <h1 className="font-display text-2xl mt-1">MRP · Material Requirements Planning</h1>
            <div className="text-xs text-zinc-500 mt-1">Pick a Sales Order + its Work Order → Calculate → Process to reserve stock & generate a Purchase Requirement. Or use the browse table below.</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="mrp-refresh" onClick={load} className={btnGhost} disabled={loading}>
              <ArrowClockwiseIcon size={12} weight="bold" />Refresh
            </button>
          </div>
        </div>

        {/* Pick-up flow — SO/ESO → its WO → Calculate */}
        <div className="tactical-card p-4 space-y-3" data-testid="mrp-pickup">
          <div className="flex items-center justify-between">
            <div className="overline text-[10px]">Pick Sales Order · Work Order</div>
            <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">{soOptions.length} SO · {woOptions.length} WO</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">SO / ESO</label>
              <select
                data-testid="mrp-pick-so"
                value={pickSoId}
                onChange={e => setPickSoId(e.target.value)}
                className="w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)]"
              >
                <option value="">— Select Sales Order —</option>
                {soOptions.map(s => (
                  <option key={s.id} value={s.id}>{s.type} · {s.no}{s.customer ? ` · ${s.customer}` : ""}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Work Order</label>
              <select
                data-testid="mrp-pick-wo"
                value={pickWoId}
                onChange={e => setPickWoId(e.target.value)}
                disabled={!pickSoId}
                className="w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{pickSoId ? (woOptions.length ? "— Select Work Order —" : "No Work Orders for this SO") : "— Pick SO first —"}</option>
                {woOptions.map(w => (
                  <option key={w.work_order_id} value={w.work_order_id}>{w.work_order_no} · {w.product_code || "—"} · {w.production_qty} · BOM {w.bom_version ? `v${w.bom_version}` : "—"}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4 flex items-center gap-2">
              <button
                data-testid="mrp-pick-calculate"
                onClick={() => picked && calculate(picked)}
                disabled={!picked || !picked.bom_id || loading}
                className={btnPrimary + " h-9"}
              >
                <LightningIcon size={12} weight="bold" />Calculate
              </button>
              {(pickSoId || pickWoId) && (
                <button
                  data-testid="mrp-pick-clear"
                  onClick={() => { setPickSoId(""); setPickWoId(""); }}
                  className={btnGhost + " h-9"}
                >
                  <XIcon size={12} weight="bold" />Clear
                </button>
              )}
            </div>
          </div>
          {picked && (
            <div data-testid="mrp-pick-summary" className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs pt-3 border-t border-[var(--border)]">
{/* Customer hidden on production floor · Mar 2026 */}
              <div className="md:col-span-2"><div className="text-zinc-500 mb-0.5">Product Code</div><div className="font-mono text-purple-300 truncate">{picked.product_code || "—"}</div></div>
              <div><div className="text-zinc-500 mb-0.5">Prod Qty</div><div className="font-mono tabular-nums">{picked.production_qty}</div></div>
              <div><div className="text-zinc-500 mb-0.5">Required</div><div className="font-mono text-[10px] text-zinc-400">{fmtDateIST(picked.required_date)}</div></div>
              <div><div className="text-zinc-500 mb-0.5">BOM / MRP</div>
                <div className="font-mono flex items-center gap-1">
                  {picked.bom_version ? <span className="text-emerald-300">v{picked.bom_version}</span> : <span className="text-red-400">No BOM</span>}
                  {picked.existing_mrp && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 uppercase tracking-widest">{picked.existing_mrp.mrp_no}</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Candidate SO / WO list */}
        <div className="tactical-card p-0">
          <div className="px-4 py-2 border-b border-[var(--border)] flex items-center justify-between gap-3">
            <div className="overline text-[10px]">SO Candidates · {candidates.length}</div>
            <div className="relative">
              <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="mrp-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search SO / WO / customer / product…" className="h-8 w-72 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
            </div>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">SO No</th>
                  <th className="px-3 py-2 text-left">WO No</th>
                  {/* Customer column hidden on production floor · Mar 2026 */}
                  <th className="px-3 py-2 text-left">Product Code</th>
                  <th className="px-3 py-2 text-right w-24">Prod Qty</th>
                  <th className="px-3 py-2 text-center w-24">Required</th>
                  <th className="px-3 py-2 text-center w-24">BOM v</th>
                  <th className="px-3 py-2 text-center w-28">MRP Status</th>
                  <th className="px-3 py-2 text-center w-32">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-xs text-zinc-500">{candidates.length ? "No matches" : "No approved / released Work Orders yet"}</td></tr>}
                {filtered.map((c, i) => (
                  <tr key={c.work_order_id} data-testid={`mrp-cand-${i}`} data-wo-no={c.work_order_no} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/40 transition">
                    <td className="px-3 py-2 text-right"><TwoLineDocNo value={c.sales_order_no || "—"} className="text-xs text-[var(--accent)]" /></td>
                    <td className="px-3 py-2 text-right"><TwoLineDocNo value={c.work_order_no || "—"} className="text-xs text-zinc-300" /></td>
                    {/* Customer cell hidden on production floor · Mar 2026 */}
                    <td className="px-3 py-2 font-mono text-xs text-purple-300 truncate max-w-[280px]">{c.product_code || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{c.production_qty}</td>
                    <td className="px-3 py-2 text-center font-mono text-[10px] text-zinc-500">{fmtDateIST(c.required_date)}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{c.bom_version ? `v${c.bom_version}` : <span className="text-red-400">No BOM</span>}</td>
                    <td className="px-3 py-2 text-center">
                      {c.existing_mrp ? <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-mono uppercase tracking-widest">{c.existing_mrp.mrp_no}</span>
                        : c.live_pr_count > 0 ? (
                          <span
                            data-testid={`mrp-cand-dup-${i}`}
                            className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40 font-mono uppercase tracking-widest"
                            title={`This Sales Order already has ${c.live_pr_count} live PR${c.live_pr_count === 1 ? "" : "s"} (Open / Approved / Pending Approval). Running MRP will likely raise duplicates for overlapping items.`}
                          >
                            Duplicate risk · {c.live_pr_count} live PR{c.live_pr_count === 1 ? "" : "s"}
                          </span>
                        ) : <span className="text-[10px] text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button data-testid={`mrp-calc-${i}`} onClick={() => calculate(c)} disabled={!c.bom_id} className={btnPrimary + " h-7 px-3"}>
                        <LightningIcon size={11} weight="bold" />Calculate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Existing MRP runs */}
        <div className="tactical-card p-0">
          <div className="px-4 py-2 border-b border-[var(--border)] overline text-[10px]">Processed MRP Runs · {runs.length}</div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">MRP No</th>
                  <th className="px-3 py-2 text-left">SO / WO</th>
                  {/* Customer column hidden on production floor · Mar 2026 */}
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right w-16">Lines</th>
                  <th className="px-3 py-2 text-center w-24">PR</th>
                  <th className="px-3 py-2 text-center w-24">Status</th>
                  <th className="px-3 py-2 text-right w-32">Date</th>
                  <th className="px-3 py-2 text-center w-28">Action</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-xs text-zinc-500">No MRP runs yet</td></tr>}
                {runs.map((r, i) => (
                  <tr key={r.id} data-testid={`mrp-run-${i}`} onClick={() => openDetail(r)} className={`border-t border-[var(--border)] cursor-pointer hover:bg-[var(--bg-2)]/40 transition ${r.needs_recalc ? "bg-red-500/5" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs text-[var(--accent)]">
                      <div className="flex items-center gap-2">
                        <span>{r.mrp_no}</span>
                        {/* Feb 27, 2026 · Needs Recalculation chip — flagged
                            by docs_ops when the linked SO / ESO is short-closed
                            or a line balance is cancelled. Tooltip surfaces
                            the exact reason from `needs_recalc_reason`. */}
                        {r.needs_recalc && (
                          <span
                            data-testid={`mrp-recalc-chip-${i}`}
                            title={r.needs_recalc_reason || "Source order changed — please recalculate this MRP run"}
                            className="inline-flex items-center gap-1 text-[8.5px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/50 font-mono uppercase tracking-widest animate-pulse"
                          >
                            <WarningCircleIcon size={9} weight="bold" />Needs Recalc
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.sales_order_no || "—"} · {r.work_order_no || "—"}</td>
                    {/* Customer cell hidden on production floor · Mar 2026 */}
                    <td className="px-3 py-2 font-mono text-xs text-purple-300 truncate max-w-[220px]">{r.product_code || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{(r.lines || []).length}</td>
                    <td className="px-3 py-2 text-center"><TwoLineDocNo value={r.pr_no || "—"} className="text-[10px]" /></td>
                    <td className="px-3 py-2 text-center"><span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-mono uppercase tracking-widest">{r.status}</span></td>
                    <td className="px-3 py-2 text-right font-mono text-[10px] text-zinc-500">{fmtDateIST(r.created_at)}</td>
                    <td className="px-3 py-2 text-center">
                      {/* Feb 25 · Delete removed — MRP runs cascade
                          production-plan → CWO history that ops need to
                          keep for audit. Use per-document reversal from
                          Reversal Audit for controlled corrections. */}
                      <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          title="View MRP run"
                          data-testid={`mrp-view-${i}`}
                          onClick={() => openDetail(r)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-sky-500/40 hover:bg-sky-500/10 text-sky-300 text-[10px] font-mono uppercase tracking-widest">
                          <EyeIcon size={11} weight="bold" />View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Calculate Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && (setSelected(null), setPreview(null))}>
        <DialogContent data-testid="mrp-preview-dialog" className="bg-[var(--bg-1)] border-[var(--border)] w-screen h-screen max-w-none max-h-none rounded-none p-6 sm:p-8 overflow-hidden flex flex-col gap-3" description="Material Requirements Planning action — review RM shortages and finalize the run.">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-display text-lg">MRP Calculation · {selected?.sales_order_no} → {selected?.work_order_no}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="flex-1 min-h-0 flex flex-col gap-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs shrink-0">
                <div><div className="text-zinc-500 mb-0.5">Product</div><div className="font-mono text-[var(--accent)]">{preview.product_code}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Qty</div><div className="font-mono">{preview.production_qty}</div></div>
                <div><div className="text-zinc-500 mb-0.5">BOM v</div><div className="font-mono">v{preview.bom_version}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Shortage Lines</div>{(() => { const _sh = (preview.lines || []).filter(l => Number(l.net_requirement || 0) > 0).length; return <div className={`font-mono ${_sh ? "text-red-400" : "text-emerald-400"}`}>{_sh}</div>; })()}</div>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-auto flex-1 min-h-0">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-center w-12">Lv</th>
                        <th className="px-3 py-2 text-left">Item</th>
                        <th className="px-3 py-2 text-right">Gross Req</th>
                        <th className="px-3 py-2 text-right">Physical</th>
                        <th className="px-3 py-2 text-right">Reserved</th>
                        <th className="px-3 py-2 text-right">Free</th>
                        {/* Mar 03, 2026 · Manual Allocation Control */}
                        <th className="px-3 py-2 text-center w-16">Allocate</th>
                        <th className="px-3 py-2 text-right w-32">Allocated Qty</th>
                        <th className="px-3 py-2 text-left w-40">Reason *</th>
                        <th className="px-3 py-2 text-right">Purchase Req</th>
                        <th className="px-3 py-2 text-center w-40">Status</th>
                        {/* Phase H1-γ-β · Proposed EXACT + APPROVED_ALTERNATE
                            allocations returned by the server preview. */}
                        <th className="px-3 py-2 text-left">Proposed Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.lines || []).map((ln, i) => {
                        const allocState = allocations[ln.rm_id] || { ticked: false, qty: 0, reason: "" };
                        const grossCap = Number(ln.gross_qty || 0);
                        const capBoth = Math.min(ln.free_stock, grossCap);
                        const alloc = allocState.ticked ? Math.max(0, Math.min(Number(allocState.qty) || 0, capBoth)) : 0;
                        // Mar 03, 2026 · Spec formula: Net Purchase Required = Gross − Manually Allocated
                        const purchaseReq = Math.max(0, Number(ln.gross_qty || 0) - alloc);
                        const purchaseColor = purchaseReq > 0 ? "text-red-400 font-semibold" : "text-emerald-400";
                        const lots = ln.lots || [];
                        // Feb 2026 · Reason is required only when the operator
                        // COULD have satisfied the requirement from free stock
                        // but chose not to. If `alloc >= gross_qty` the line is
                        // fully covered — no explanation needed for the leftover
                        // surplus. Also skip when there simply isn't any free stock.
                        const needsReason = Number(ln.free_stock || 0) > 0 && alloc < grossCap && alloc < Number(ln.free_stock || 0);
                        return (
                          <tr key={i} data-testid={`mrp-line-${i}`} className={`border-t border-[var(--border)] ${purchaseReq > 0 ? "bg-red-500/5" : ""}`}>
                            <td className="px-3 py-2 text-center" data-testid={`mrp-line-${i}-level`}>
                              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${(ln.bom_level || 1) === 1 ? "border-sky-500/40 text-sky-300 bg-sky-500/10" : "border-violet-500/40 text-violet-300 bg-violet-500/10"}`}>L{ln.bom_level || 1}</span>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs text-[var(--accent)]">{ln.rm_code || "—"}</span>
                                {allocState.auto && (
                                  <span data-testid={`mrp-auto-badge-${i}`}
                                        title={`Auto-allocated: ${(allocState.auto_reasons || []).map(r => r === "PO_LINKED" ? "PO linked to this SO" : r === "OPENING_RESERVED" ? "Opening stock reserved for this SO" : r).join(" · ")}`}
                                        className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/50 text-cyan-300 bg-cyan-500/10 rounded-sm">
                                    Auto
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-zinc-500 truncate max-w-[280px]">{ln.rm_name}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(ln.gross_qty)} {ln.uom}</td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmt(ln.physical_stock)}</td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{fmt(ln.reserved_stock)}</td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-400">{fmt(ln.free_stock)}</td>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox" data-testid={`mrp-alloc-tick-${i}`}
                                     disabled={ln.free_stock <= 0}
                                     checked={!!allocState.ticked}
                                     onChange={e => {
                                       const on = e.target.checked;
                                       const capBothLocal = Math.min(ln.free_stock, Number(ln.gross_qty || 0));
                                       if (on && lots.length > 1) {
                                         // Multi-batch RM → force operator to explicitly pick lots.
                                         setAllocations(a => ({ ...a, [ln.rm_id]: { ...(a[ln.rm_id] || {}), ticked: false, qty: 0, selected_lots: {}, auto: false } }));
                                         setBatchPopup({ ln, idx: i });
                                       } else {
                                         // Single-lot (or untick) → simple auto-fill.
                                         const singleSel = {};
                                         if (on && lots.length === 1) singleSel[lots[0].lot_id || lots[0].lot_no] = Number(lots[0].qty_available) || capBothLocal;
                                         setAllocations(a => ({ ...a, [ln.rm_id]: { ...(a[ln.rm_id] || {}), ticked: on, qty: on ? capBothLocal : 0, selected_lots: on ? singleSel : {}, auto: false } }));
                                       }
                                     }}
                                     className="h-4 w-4 accent-emerald-500 cursor-pointer disabled:opacity-30" />
                              {lots.length > 1 && (
                                <button data-testid={`mrp-batches-${i}`} onClick={() => setBatchPopup({ ln, idx: i })}
                                        className="ml-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-300 rounded-sm hover:bg-amber-500/10"
                                        title={`${lots.length} batches · click for breakdown`}>
                                  {lots.length} btch
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input data-testid={`mrp-alloc-qty-${i}`} type="number" step="0.01" min={0} max={capBoth}
                                     value={allocState.ticked ? allocState.qty : 0}
                                     disabled={!allocState.ticked}
                                     onChange={e => setAllocations(a => ({ ...a, [ln.rm_id]: { ...(a[ln.rm_id] || {}), ticked: true, qty: Math.min(Number(e.target.value) || 0, capBoth), auto: false } }))}
                                     className="w-24 h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums disabled:opacity-40" />
                              {/* Feb 2026 · Show which batches this row is
                                  drawing from — critical for dye-lot
                                  traceability so the operator sees at a
                                  glance which physical lots got locked. */}
                              {allocState.ticked && Object.keys(allocState.selected_lots || {}).length > 0 && (
                                <div data-testid={`mrp-picked-batches-${i}`} className="mt-1 flex flex-wrap gap-1 justify-end max-w-[220px] ml-auto">
                                  {Object.entries(allocState.selected_lots).map(([lotId, q]) => {
                                    const lot = (ln.lots || []).find(x => (x.lot_id || x.lot_no) === lotId);
                                    if (!lot) return null;
                                    const label = lot.batch_no || lot.lot_no || lotId.slice(0, 6);
                                    return (
                                      <span key={lotId}
                                            className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-purple-500/40 text-purple-300 bg-purple-500/10 rounded-sm"
                                            title={`Lot ${lot.lot_no} · Batch ${lot.batch_no || "—"} · ${fmt(q)} ${ln.uom}`}>
                                        {label} · {fmt(q, 2)}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {needsReason ? (
                                <select data-testid={`mrp-alloc-reason-${i}`} value={allocState.reason || ""}
                                        onChange={e => setAllocations(a => ({ ...a, [ln.rm_id]: { ...(a[ln.rm_id] || {}), reason: e.target.value } }))}
                                        className={`w-full h-7 px-1.5 bg-[var(--bg-0)] border rounded-sm text-[10px] font-mono uppercase tracking-widest ${!allocState.reason ? "border-amber-500/70 text-amber-300" : "border-[var(--border)] text-zinc-300"}`}>
                                  <option value="">— pick reason * —</option>
                                  <option value="Batch Incompatibility">Batch Incompatibility</option>
                                  <option value="Insufficient Package Size">Insufficient Package Size</option>
                                  <option value="Damaged/Hold">Damaged/Hold</option>
                                  <option value="Other">Other</option>
                                </select>
                              ) : <span className="text-[9px] font-mono text-zinc-600">—</span>}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${purchaseColor}`} data-testid={`mrp-purchase-req-${i}`}>{fmt(purchaseReq)}</td>
                            <td className="px-3 py-2 text-center"><span className={`text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-widest ${STATUS_STYLE[purchaseReq > 0 ? "SHORT" : "OK"] || "border-[var(--border)] text-zinc-400"}`}>{purchaseReq > 0 ? "SHORT" : "OK"}</span></td>
                            <td className="px-3 py-2" data-testid={`mrp-proposed-alloc-${i}`}>
                              {(ln.proposed_allocations || []).length === 0
                                ? <span className="text-[9px] font-mono text-zinc-600">—</span>
                                : (
                                  <div className="flex flex-wrap gap-1">
                                    {(ln.proposed_allocations || []).map((pa, pi) => (
                                      <span key={pi}
                                            title={pa.allocation_type === "APPROVED_ALTERNATE"
                                              ? `Alternate ${pa.allocated_rm_code || pa.allocated_rm_id} · RMA ${pa.alternate_approval_no || ""} · P${pa.alternate_priority || ""}`
                                              : `Exact · ${pa.allocated_rm_code || ln.rm_code}`}
                                            className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${pa.allocation_type === "APPROVED_ALTERNATE"
                                              ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                                              : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>
                                        {pa.allocation_type === "APPROVED_ALTERNATE" ? "ALT" : "EXACT"} · {fmt(pa.allocated_qty, 2)}
                                        {pa.allocation_type === "APPROVED_ALTERNATE" && pa.allocated_rm_code
                                          ? ` → ${pa.allocated_rm_code}` : ""}
                                      </span>
                                    ))}
                                    {(ln.proposed_shortage || 0) > 0 && (
                                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300"
                                            title="Residual shortage after Exact + Approved Alternates">
                                        PURCHASE · {fmt(ln.proposed_shortage, 2)}
                                      </span>
                                    )}
                                  </div>
                                )
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
            </div>
          )}
          <DialogFooter className="pt-2 shrink-0">
            <button data-testid="mrp-preview-cancel" onClick={() => { setSelected(null); setPreview(null); setAllocations({}); }} className={btnGhost} disabled={processing}>
              <XIcon size={12} weight="bold" />Cancel
            </button>
            <button data-testid="mrp-process" onClick={() => process(false)} disabled={processing} className={btnPrimary + " bg-emerald-600 hover:bg-emerald-500"}>
              <CheckCircleIcon size={13} weight="bold" />{processing ? "Processing…" : "Process MRP · Reserve & Generate PR"}
            </button>
            {/* Phase H1-γ-β · Commit the server-proposed EXACT + APPROVED_ALTERNATE
                allocations using the multi-leg saga path with confirmation_id
                idempotency + confirm-time RMA revalidation. */}
            <button data-testid="mrp-confirm-alternates" onClick={() => processWithAlternates(false)}
                    disabled={processing || !preview || !(preview.lines || []).some(l => (l.proposed_allocations || []).length > 0)}
                    className={btnPrimary + " bg-amber-600 hover:bg-amber-500"}
                    title="Reserve using server-proposed Exact + Approved Alternates">
              <CheckCircleIcon size={13} weight="bold" />
              {processing ? "Committing…" : "Confirm Alternates · Reserve H1-γ-β"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mar 03, 2026 · Batch Breakdown popup — shows per-lot availability
          so the operator can decide whether mixing multiple small batches
          is acceptable before ticking allocation. */}
      <Dialog open={!!batchPopup} onOpenChange={(o) => !o && setBatchPopup(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-amber-500/40 max-w-lg" data-testid="mrp-batch-popup"
          description="Batch-wise availability breakdown for this raw material.">
          <DialogHeader>
            <DialogTitle className="font-display text-base flex items-center gap-2">
              <WarningIcon size={16} weight="bold" className="text-amber-400" />
              Batch Breakdown · {batchPopup?.ln?.rm_code}
            </DialogTitle>
          </DialogHeader>
          {batchPopup && (() => {
            const lnLots = batchPopup.ln.lots || [];
            const rmId = batchPopup.ln.rm_id;
            const cur = allocations[rmId] || {};
            const selectedLots = cur.selected_lots || {};   // { lot_id: qty }
            const pickedIds = Object.keys(selectedLots);
            const pickedQty = pickedIds.reduce((s, k) => s + (Number(selectedLots[k]) || 0), 0);
            const toggleLot = (l, on) => {
              setAllocations(a => {
                const prev = a[rmId] || {};
                const sl = { ...(prev.selected_lots || {}) };
                const cap = Math.min(Number(batchPopup.ln.free_stock || 0), Number(batchPopup.ln.gross_qty || 0));
                if (on) {
                  // Feb 2026 · Cap per-lot pick so total never exceeds requirement.
                  // Only take the slice we still need from this batch; if the
                  // requirement is already fully covered by previously-ticked
                  // batches, the new pick contributes zero (still tracked so
                  // the UI stays consistent).
                  const takenSoFar = Object.entries(sl).reduce((s, [k, v]) => s + (Number(v) || 0), 0);
                  const room = Math.max(0, cap - takenSoFar);
                  const slice = Math.min(Number(l.qty_available) || 0, room);
                  sl[l.lot_id || l.lot_no] = round3(slice);
                } else {
                  delete sl[l.lot_id || l.lot_no];
                }
                const totalQty = round3(Object.values(sl).reduce((s, v) => s + (Number(v) || 0), 0));
                const hasAny = Object.keys(sl).length > 0;
                return { ...a, [rmId]: { ...prev, ticked: hasAny, qty: totalQty, selected_lots: sl, auto: false } };
              });
            };
            const allChecked = pickedIds.length === lnLots.length && lnLots.length > 0;
            const toggleAll = (on) => {
              setAllocations(a => {
                const prev = a[rmId] || {};
                const sl = {};
                const cap = Math.min(Number(batchPopup.ln.free_stock || 0), Number(batchPopup.ln.gross_qty || 0));
                if (on) {
                  let remaining = cap;
                  lnLots.forEach(l => {
                    if (remaining <= 0) return;
                    const slice = Math.min(Number(l.qty_available) || 0, remaining);
                    if (slice > 0) {
                      sl[l.lot_id || l.lot_no] = round3(slice);
                      remaining -= slice;
                    }
                  });
                }
                const totalQty = round3(Object.values(sl).reduce((s, v) => s + (Number(v) || 0), 0));
                return { ...a, [rmId]: { ...prev, ticked: on, qty: totalQty, selected_lots: sl, auto: false } };
              });
            };
            return (
              <div className="space-y-2">
                <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">
                  {lnLots.length} lot(s) available · total {fmt(batchPopup.ln.free_stock)} {batchPopup.ln.uom}
                  {lnLots.length > 1 && <span className="text-amber-400"> · mixing multiple batches may affect yarn consistency</span>}
                </div>
                <table className="w-full text-xs border border-[var(--border)]">
                  <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="px-2 py-1 text-center w-8">
                        <input type="checkbox" data-testid="mrp-batch-tick-all"
                               checked={allChecked}
                               onChange={e => toggleAll(e.target.checked)}
                               className="h-3.5 w-3.5 accent-emerald-500 cursor-pointer" />
                      </th>
                      <th className="px-2 py-1 text-left">Lot No</th>
                      <th className="px-2 py-1 text-left">Batch</th>
                      <th className="px-2 py-1 text-left">Shade</th>
                      <th className="px-2 py-1 text-left">Received</th>
                      <th className="px-2 py-1 text-right">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lnLots.map((l, j) => {
                      const key = l.lot_id || l.lot_no;
                      const checked = key in selectedLots;
                      return (
                        <tr key={j} className={`border-t border-[var(--border)] ${checked ? "bg-emerald-500/5" : ""}`}>
                          <td className="px-2 py-1 text-center">
                            <input type="checkbox" data-testid={`mrp-batch-tick-${j}`}
                                   checked={checked}
                                   onChange={e => toggleLot(l, e.target.checked)}
                                   className="h-3.5 w-3.5 accent-emerald-500 cursor-pointer" />
                          </td>
                          <td className="px-2 py-1 font-mono text-emerald-300">
                            {l.lot_no}
                            {l.auto_reason && (
                              <span className="ml-1 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-cyan-500/50 text-cyan-300 bg-cyan-500/10 rounded-sm"
                                    title={l.auto_reason === "PO_LINKED" ? "PO linked to this SO" : "Opening stock reserved for this SO"}>
                                Auto
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 font-mono">{l.batch_no || "—"}</td>
                          <td className="px-2 py-1 font-mono">{l.shade_no || "—"}</td>
                          <td className="px-2 py-1 font-mono text-zinc-400">{fmtDateIST(l.received_date)}</td>
                          <td className="px-2 py-1 text-right font-mono tabular-nums">{fmt(l.qty_available)} {batchPopup.ln.uom}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    Selected: <span className="text-emerald-300">{pickedIds.length}</span> lot(s) · <span className="text-emerald-300 tabular-nums">{fmt(pickedQty)} {batchPopup.ln.uom}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setBatchPopup(null)} className={btnGhost}>Close</button>
                    <button data-testid="mrp-batch-confirm" disabled={pickedIds.length === 0}
                            onClick={() => setBatchPopup(null)}
                            className={btnPrimary + " bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"}>
                      Confirm Selection ({fmt(pickedQty)})
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · PR Clash Dialog — 3-choice resolver
          (Overwrite existing PR · Create Duplicate · Cancel).
          Replaces the old OK/Cancel confirm() so the buyer can pick
          the correct treatment without having to delete the old PR by
          hand. */}
      <Dialog open={!!prClash} onOpenChange={(o) => !o && setPrClash(null)}>
        <DialogContent data-testid="mrp-pr-clash-dialog" className="bg-[var(--bg-1)] border-amber-500/60 max-w-xl" description="Material Requirements Planning action — review RM shortages and finalize the run.">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <WarningIcon size={18} weight="bold" className="text-amber-400" />
              Purchase Requisition Already Exists
            </DialogTitle>
          </DialogHeader>
          {prClash && (
            <div className="space-y-3 pt-2">
              <div className="text-[11px] text-zinc-400">A live PR is already covering some of the RMs on this Work Order. Pick how to proceed:</div>
              {(prClash.clashes || []).length > 0 && (
                <div className="border border-[var(--border)] rounded-sm p-3 bg-amber-500/5 space-y-2 max-h-52 overflow-y-auto">
                  {prClash.clashes.map((c, i) => (
                    <div key={i} className="text-[11px]" data-testid={`mrp-pr-clash-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[var(--accent)]">{c.pr_no}</span>
                        <span className="text-[9px] px-2 py-0.5 rounded-sm border border-[var(--border)] text-zinc-400 font-mono uppercase tracking-widest">{c.status}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5 break-words">
                        {(c.rm_codes || []).slice(0, 6).join(" · ")}{(c.rm_codes || []).length > 6 ? " …" : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-zinc-500 leading-relaxed border-l-2 border-amber-500/60 pl-3">
                <b className="text-amber-300">Overwrite</b> — marks the existing PR(s) as <span className="font-mono">SUPERSEDED</span> and creates a fresh one. Use this when the earlier PR was wrong or is being replaced.<br />
                <b className="text-sky-300">Create Duplicate</b> — keeps the old PR intact and raises a second, independent PR alongside. Use only when both PRs need to remain live.
              </div>
            </div>
          )}
          <DialogFooter className="pt-2 gap-2 flex-wrap">
            <button data-testid="mrp-pr-clash-cancel" onClick={() => setPrClash(null)} className={btnGhost} disabled={processing}>
              <XIcon size={12} weight="bold" />Cancel
            </button>
            <button data-testid="mrp-pr-clash-duplicate" onClick={() => { setPrClash(null); process(false, true, false); }} disabled={processing}
                    className="h-9 px-4 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 text-xs font-mono uppercase tracking-widest rounded-sm inline-flex items-center gap-1.5">
              <CopyIcon size={12} weight="bold" />Create Duplicate
            </button>
            <button data-testid="mrp-pr-clash-overwrite" onClick={() => { setPrClash(null); process(false, false, true); }} disabled={processing}
                    className="h-9 px-4 bg-amber-600 hover:bg-amber-500 text-white text-xs font-mono uppercase tracking-widest rounded-sm inline-flex items-center gap-1.5">
              <ArrowsClockwiseIcon size={12} weight="bold" />Overwrite Existing PR
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MRP Run Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent data-testid="mrp-detail-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-[95vw] max-h-[92vh] overflow-y-auto" description="Material Requirements Planning action — review RM shortages and finalize the run.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <TreeStructureIcon size={20} weight="bold" className="text-sky-400" />
              MRP {detail?.mrp_no} · {detail?.product_code}
              {detailLoading && <span className="text-xs text-zinc-500">· loading hierarchy…</span>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><div className="text-zinc-500 mb-0.5">SO / WO</div><div className="font-mono text-zinc-300">{detail.sales_order_no} · {detail.work_order_no}</div></div>
{/* Customer hidden on production floor · Mar 2026 */}
                <div><div className="text-zinc-500 mb-0.5">Qty</div><div className="font-mono">{detail.production_qty}</div></div>
                <div><div className="text-zinc-500 mb-0.5">PR</div><div className="font-mono text-emerald-400">{detail.pr_no || "—"}</div></div>
              </div>

              {/* Hierarchical explosion — each BOM level rendered like the "All BOMs" view. */}
              {detailLevels?.levels?.length ? detailLevels.levels.map((sec, si) => (
                <div key={si} className="border border-[var(--border)] rounded-sm overflow-hidden" data-testid={`mrp-level-${sec.level}`}>
                  <div className="px-3 py-2 bg-[var(--bg-2)]/60 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-xs">
                      <StackIcon size={12} weight="bold" className={sec.level === 1 ? "text-sky-400" : "text-purple-400"} />
                      <span className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">
                        BOM Level {sec.level}
                      </span>
                      <span className="text-white font-mono">{sec.product_code}</span>
                      {sec.parent_sf_code && <span className="text-zinc-500">under SF · <span className="font-mono text-zinc-300">{sec.parent_sf_code}</span></span>}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500">
                      Qty: <span className="text-white">{fmt(sec.qty)}</span> {sec.output_uom || ""}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[var(--bg-2)]/30 text-zinc-500 uppercase tracking-widest text-[10px]">
                        <tr>
                          <th className="px-2 py-2 text-left w-14">Type</th>
                          <th className="px-2 py-2 text-left">Code · Name</th>
                          <th className="px-2 py-2 text-left w-20">Routing</th>
                          <th className="px-2 py-2 text-right w-24">Gross Req</th>
                          <th className="px-2 py-2 text-right w-24">Free Stock</th>
                          <th className="px-2 py-2 text-right w-24">Open PO</th>
                          <th className="px-2 py-2 text-right w-24">Net Req</th>
                          <th className="px-2 py-2 text-center w-32">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sec.lines || []).map((ln, li) => {
                          const overlay = ln.item_type === "RM" ? runLineByRmId.get(ln.rm_id) : null;
                          const free = overlay?.free_stock ?? 0;
                          const openPo = overlay?.open_po_qty ?? 0;
                          const netReq = overlay?.net_requirement ?? 0;
                          const status = overlay?.status || (ln.item_type === "SF" ? "SUB-ASSEMBLY" : "—");
                          return (
                            <tr key={li} className="border-t border-[var(--border)]/60">
                              <td className="px-2 py-2">
                                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${ln.item_type === "SF" ? "border-purple-500/40 bg-purple-500/10 text-purple-300" : "border-sky-500/40 bg-sky-500/10 text-sky-300"}`}>{ln.item_type}</span>
                              </td>
                              <td className="px-2 py-2">
                                <div className="font-mono text-[var(--accent)]">{ln.item_code}</div>
                                <div className="text-[10px] text-zinc-500">{ln.item_name || ""}</div>
                              </td>
                              <td className="px-2 py-2 font-mono text-[10px] text-zinc-400">{ln.routing || "—"}</td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums text-white">{fmt(ln.gross_qty)} <span className="text-zinc-500">{ln.uom}</span></td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-400">{ln.item_type === "RM" ? fmt(free) : "—"}</td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums text-blue-400">{ln.item_type === "RM" ? fmt(openPo) : "—"}</td>
                              <td className={`px-2 py-2 text-right font-mono tabular-nums ${netReq > 0 ? "text-red-400" : "text-emerald-400"}`}>{ln.item_type === "RM" ? fmt(netReq) : "—"}</td>
                              <td className="px-2 py-2 text-center">
                                <span className={`text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-widest ${STATUS_STYLE[status] || "border-zinc-500/30 text-zinc-400"}`}>{status}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )) : (
                /* Fallback — flat RM list when explode fails or BOM has no SF children. */
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                      <tr>
                        <th className="px-3 py-2 text-left">Item</th>
                        <th className="px-3 py-2 text-right">Gross</th>
                        <th className="px-3 py-2 text-right">Free</th>
                        <th className="px-3 py-2 text-right">Allocated</th>
                        <th className="px-3 py-2 text-left">Non-Alloc Reason</th>
                        <th className="px-3 py-2 text-right">Purchase Req</th>
                        <th className="px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.lines || []).map((ln, i) => (
                        <tr key={i} className="border-t border-[var(--border)]" data-testid={`mrp-detail-line-${i}`}>
                          <td className="px-3 py-2 font-mono text-xs text-[var(--accent)]">{ln.rm_code}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(ln.gross_qty)} {ln.uom}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-400">{fmt(ln.free_stock)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-300" data-testid={`mrp-detail-alloc-${i}`}>{fmt(ln.allocated_qty || 0)}</td>
                          <td className="px-3 py-2 text-[10px]" data-testid={`mrp-detail-reason-${i}`}>
                            {ln.non_allocation_reason
                              ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 font-mono uppercase tracking-widest">{ln.non_allocation_reason}</span>
                              : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums ${ln.net_requirement > 0 ? "text-red-400" : "text-emerald-400"}`}>{fmt(ln.net_requirement)}</td>
                          <td className="px-3 py-2 text-center"><span className={`text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-widest ${STATUS_STYLE[ln.status]}`}>{ln.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button data-testid="mrp-close" onClick={closeDetail} className={btnGhost}>
              <XIcon size={12} weight="bold" />Close
            </button>
            <button data-testid="mrp-print-pdf" onClick={printMrpPdf} disabled={!detail?.bom_id} className={btnPrimary}>
              <PrinterIcon size={12} weight="bold" />Print / Export PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
